/**
 * Pure presentation helpers shared by the Connect plugin surfaces (PR INS-2).
 *
 * Kept separate from the components so the wording rules that MATTER can be
 * unit-tested directly:
 *
 *  - an unsupported component is never described as something Sphinx runs;
 *  - a component's health is derived from the backend's `readiness` enum, not
 *    guessed from local state; and
 *  - a plugin's rolled-up health never reports "ready" while any component is
 *    not, and never reports anything at all before a version is finalized.
 */

import type {
  PluginComponentReadiness,
  PluginSetupStatus,
  PluginSummary,
} from "@/lib/plugins/plugin-api-types";

// ---------------------------------------------------------------------------
// Component readiness.
// ---------------------------------------------------------------------------

export interface PluginReadinessPresentation {
  label: string;
  /** One-line explanation of what the user has to do, if anything. */
  detail: string;
  tone: "ready" | "attention" | "info";
}

/**
 * Backend `PluginComponentReadiness` → user-facing wording.
 *
 * `local_runtime_required` / `computer_required` are placement facts, not
 * failures: the component is fine, this client just cannot be the one to run
 * it. They read as informational rather than as errors.
 */
export function describePluginReadiness(
  readiness: PluginComponentReadiness,
): PluginReadinessPresentation {
  switch (readiness) {
    case "ready":
      return {
        label: "Ready",
        detail: "No setup required before use.",
        tone: "ready",
      };
    case "needs_auth":
      return {
        label: "Needs auth",
        detail: "Authorize this server before its first tool call.",
        tone: "attention",
      };
    case "needs_setup":
      return {
        label: "Needs configuration",
        detail: "Provide the required environment or header values.",
        tone: "attention",
      };
    case "local_runtime_required":
      return {
        label: "Local runtime",
        detail: "Runs only from a local Sphinx process.",
        tone: "info",
      };
    case "computer_required":
      return {
        label: "Computer required",
        detail: "Runs only inside a Project Computer.",
        tone: "info",
      };
    default:
      // A readiness value this client does not know yet. A neutral "setup
      // required" never claims readiness; the raw code stays visible in the
      // detail line so the state is still diagnosable.
      return {
        label: "Setup required",
        detail: `Unrecognized component state "${String(readiness)}" — additional setup may be required.`,
        tone: "attention",
      };
  }
}

export function describePluginPlacement(
  placement: "remote" | "local" | "computer",
): string {
  switch (placement) {
    case "remote":
      return "Remote";
    case "local":
      return "Local";
    case "computer":
      return "Computer";
  }
}

// ---------------------------------------------------------------------------
// Plugin-level health rollup.
// ---------------------------------------------------------------------------

export type PluginHealth =
  | { kind: "disabled" }
  /** Installed, but no revision has been made active yet. */
  | { kind: "not_activated" }
  /** Setup status has not loaded (or the version is still staging). */
  | { kind: "unknown" }
  | { kind: "ready"; componentCount: number }
  | { kind: "needs_attention"; readiness: PluginComponentReadiness };

/**
 * Roll a plugin's server components up into one status line.
 *
 * Ordering is deliberately pessimistic: `needs_auth` beats the placement
 * states, which beat `ready`. A plugin is only "Ready" when EVERY component
 * is, so the card can never imply a plugin is usable while one of its servers
 * still needs authorization.
 */
const READINESS_SEVERITY: Record<string, number> = {
  needs_auth: 3,
  needs_setup: 3,
  computer_required: 2,
  local_runtime_required: 2,
  ready: 0,
};

export function rollUpPluginHealth(
  plugin: Pick<PluginSummary, "enabled" | "activeVersionId">,
  setupStatus: PluginSetupStatus | undefined,
): PluginHealth {
  if (!plugin.enabled) return { kind: "disabled" };
  if (!plugin.activeVersionId) return { kind: "not_activated" };
  // `undefined` is "still loading"; a staging version is not runtime-visible,
  // so neither state may be rendered as ready.
  if (setupStatus === undefined) return { kind: "unknown" };
  if (setupStatus.status !== "ready") return { kind: "unknown" };

  let worst: PluginComponentReadiness | null = null;
  let worstScore = -1;
  for (const component of setupStatus.components) {
    const score = READINESS_SEVERITY[component.readiness] ?? 4;
    if (score > worstScore) {
      worstScore = score;
      worst = component.readiness;
    }
  }
  if (worst === null || worstScore === 0) {
    return { kind: "ready", componentCount: setupStatus.components.length };
  }
  return { kind: "needs_attention", readiness: worst };
}

/**
 * `detail` accompanies every rollup so a surface that renders only the badge
 * can still expose WHY (as a title/tooltip). Without it an unrecognized
 * backend readiness — whose label is the generic "Setup required" — would be
 * indistinguishable from `needs_setup`, with the raw code nowhere on the card.
 */
export function describePluginHealth(health: PluginHealth): {
  label: string;
  detail: string;
  tone: "ready" | "attention" | "info" | "muted";
} {
  switch (health.kind) {
    case "disabled":
      return {
        label: "Disabled",
        detail: "This plugin is disabled for the project.",
        tone: "muted",
      };
    case "not_activated":
      return {
        label: "No active revision",
        detail: "No revision has been activated yet.",
        tone: "info",
      };
    case "unknown":
      return {
        label: "Checking…",
        detail: "Component readiness has not loaded yet.",
        tone: "muted",
      };
    case "ready":
      return {
        label: "Ready",
        detail: "Every component is ready to use.",
        tone: "ready",
      };
    case "needs_attention": {
      const described = describePluginReadiness(health.readiness);
      return {
        label: described.label,
        detail: described.detail,
        tone: described.tone,
      };
    }
  }
}

// ---------------------------------------------------------------------------
// Unavailable pinned versions (runtime preflight, INS-4).
// ---------------------------------------------------------------------------

/**
 * Backend `resolvePluginVersionsRuntime` unavailable reason → wording.
 *
 * These come from the RUNTIME probe (`plugins.resolvePluginRuntimePreview`
 * `unavailableComponents`), not from setup status: they answer "why won't this
 * exact pinned version run", which is a lifecycle fact (deleted, disabled,
 * uninstalled, still staging) rather than a readiness one. Kept beside
 * `describePluginReadiness` so a surface never has to choose between two
 * vocabularies for the same plugin.
 */
export function describePluginUnavailableReason(reason: string): {
  label: string;
  detail: string;
} {
  switch (reason) {
    case "disabled":
      return {
        label: "Disabled",
        detail:
          "The plugin is disabled for this project. Re-enable it in Connect → Plugins.",
      };
    case "uninstalled":
      return {
        label: "Uninstalled",
        detail:
          "The plugin was removed from this project. Re-import it, or unpin the version.",
      };
    case "not_ready":
      return {
        label: "Not ready",
        detail: "This revision never finished importing, so it cannot run.",
      };
    case "not_found":
      return {
        label: "Not found",
        detail: "This revision no longer exists in this project.",
      };
    default:
      // An unknown reason is still a refusal to run. Report the raw code
      // rather than implying the version is usable.
      return {
        label: reason,
        detail: "This revision cannot run right now.",
      };
  }
}

// ---------------------------------------------------------------------------
// Unsupported components.
// ---------------------------------------------------------------------------

/**
 * The ONE sentence that describes what Sphinx does with a component it does
 * not run. Centralized so the "preserved, never executed" promise cannot drift
 * between the preview and the post-install summary — the plan forbids claiming
 * OpenAI runtime parity for components Sphinx preserves but does not execute.
 */
export const PLUGIN_UNSUPPORTED_EXPLANATION =
  "Kept in the stored bundle for fidelity. Sphinx does not run these.";

/** Human label for a parser `unsupported.kind`, defaulting to the raw kind. */
export function describeUnsupportedKind(kind: string): string {
  switch (kind) {
    case "hook":
    case "hooks":
      return "Lifecycle hooks";
    case "browser_extension":
      return "Browser extension";
    case "scheduled_task":
      return "Scheduled task template";
    case "unknown_field":
      return "Unrecognized manifest field";
    default:
      return kind;
  }
}

// ---------------------------------------------------------------------------
// Skipped components (parser failure isolation).
// ---------------------------------------------------------------------------

/**
 * Human label for a skipped component's `kind` (sdk `PluginSkippedComponent`),
 * defaulting to the raw kind. Distinct from "unsupported": an unsupported
 * component is preserved by design, a skipped one FAILED validation and is
 * not installed at all — which is why the preview surfaces skips loudly.
 */
export function describeSkippedComponentKind(kind: string): string {
  switch (kind) {
    case "server":
      return "MCP server";
    case "skill":
      return "Skill";
    case "mcp-config":
      return "MCP configuration";
    default:
      return kind;
  }
}

// ---------------------------------------------------------------------------
// Misc formatting.
// ---------------------------------------------------------------------------

/** Short, copy-friendly revision label for a version's content identity. */
export function shortBundleHash(bundleHash: string): string {
  return bundleHash.slice(0, 12);
}

export function formatByteSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function pluralize(count: number, singular: string, plural?: string) {
  return `${count} ${count === 1 ? singular : (plural ?? `${singular}s`)}`;
}
